import { useRef } from 'react';

import PropTypes from 'prop-types';
import { BsPlus, BsKanban, BsListUl, BsFunnel } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import Select from '#globalComponents/Forms/Select';

import PipelineFilters from '../PipelineFilters/PipelineFilters';

const PipelineToolbar = ({
	pipelines,
	activePipelineId,
	onPipelineChange,
	viewMode,
	onViewModeChange,
	filters,
	onFilterChange,
	onResetFilters,
	members,
	tags,
	activeFilterCount,
	filterOpen,
	onFilterToggle,
	onNewDeal,
}) => {
	const filterButtonRef = useRef(null);

	return (
		<>
			<Card className="flex items-center justify-between px-4 py-2.5 mb-3.5">
				<div className="flex items-center gap-2.5">
					{pipelines.length > 1 && (
						<Select
							value={activePipelineId ?? ''}
							onChange={(e) => onPipelineChange(e.target.value)}
							className="min-w-[180px]"
						>
							{pipelines.map((p) => (
								<option key={p.id} value={p.id}>
									{p.name}
								</option>
							))}
						</Select>
					)}

					<div className="flex bg-bgBase-300 border border-bgBase-500 rounded-control p-0.5 gap-0.5">
						<button
							type="button"
							onClick={() => onViewModeChange('kanban')}
							className={`px-2.5 py-1 rounded-control text-xs font-medium transition-colors ${viewMode === 'kanban' ? 'bg-white text-primary-700' : 'text-bgBase-800 hover:text-text'}`}
						>
							<BsKanban className="w-3.5 h-3.5" />
						</button>
						<button
							type="button"
							onClick={() => onViewModeChange('list')}
							className={`px-2.5 py-1 rounded-control text-xs font-medium transition-colors ${viewMode === 'list' ? 'bg-white text-primary-700' : 'text-bgBase-800 hover:text-text'}`}
						>
							<BsListUl className="w-3.5 h-3.5" />
						</button>
					</div>

					<div className="relative">
						<button
							ref={filterButtonRef}
							type="button"
							onClick={onFilterToggle}
							className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium rounded-control border transition-colors ${
								activeFilterCount > 0
									? 'border-primary-400 text-primary-700 bg-primary-100'
									: 'border-bgBase-500 text-text bg-white hover:bg-bgBase-300 hover:border-bgBase-600'
							}`}
						>
							<BsFunnel className="w-3 h-3" /> Filter
							{activeFilterCount > 0 && (
								<span className="inline-grid place-items-center min-w-[16px] h-4 px-1 rounded-full bg-primary-600 text-white text-[10px] font-bold">
									{activeFilterCount}
								</span>
							)}
						</button>
						{filterOpen && (
							<PipelineFilters
								filters={filters}
								onChange={onFilterChange}
								members={members}
								tags={tags}
								activeFilterCount={activeFilterCount}
								onClose={onFilterToggle}
								onReset={onResetFilters}
								anchorRef={filterButtonRef}
							/>
						)}
					</div>
				</div>
				<Button variant="solid" size="sm" onClick={onNewDeal}>
					<BsPlus className="w-4 h-4 mr-1" />
					Neuer Deal
				</Button>
			</Card>
		</>
	);
};

PipelineToolbar.propTypes = {
	pipelines: PropTypes.array.isRequired,
	activePipelineId: PropTypes.string,
	onPipelineChange: PropTypes.func.isRequired,
	viewMode: PropTypes.string.isRequired,
	onViewModeChange: PropTypes.func.isRequired,
	filters: PropTypes.object.isRequired,
	/** Receives a partial filter update; undefined removes a key. */
	onFilterChange: PropTypes.func.isRequired,
	onResetFilters: PropTypes.func.isRequired,
	members: PropTypes.array.isRequired,
	tags: PropTypes.array,
	activeFilterCount: PropTypes.number.isRequired,
	filterOpen: PropTypes.bool.isRequired,
	onFilterToggle: PropTypes.func.isRequired,
	onNewDeal: PropTypes.func.isRequired,
};

export default PipelineToolbar;
